import {
  PMaxCore,
  AuthenticationManager,
  Account,
  Actor,
  PermissionContext,
  RoleNames,
} from 'core';
import { NetworkState } from 'components/NetworkState';
import {
  UpdateEventListener,
  FireableUpdateEventListener,
} from 'utils/UpdateEventListener';
import {
  AppHeaderModel,
  DefaultAppHeaderModel,
} from 'containers/App/AppHeader';
import {
  DefaultAppMenuBarModel,
  AppMenuBarModel,
} from 'containers/App/AppMenuBar';
import defaultMenus from './defaultMenus';
import { OrdersModel, DefaultOrdersModel } from 'containers/Orders/OrdersModel';
import {
  CreativesModel,
  DefaultCreativesModel,
} from 'containers/Creatives/CreativesModel';
import {
  SessionStorageHelper,
  SessionStorageItemKeys,
} from 'helper/StorageHelper';
import {
  AdvertisersModel,
  DefaultAdvertisersModel,
} from 'containers/Advertisers/AdvertisersModel';
import {
  DefaultAgenciesModel,
  AgenciesModel,
} from 'containers/Agencies/AgenciesModel';
import {
  AccountsModel,
  DefaultAccountsModel,
} from 'containers/Accounts/AccountsModel';
import config from 'config';
import {
  DefaultRetailMomoModel,
  RetailMomoModel,
} from 'containers/RetailMomo/RetailMomoModel';
import { userStore } from 'store/user';

type AlertData = {
  message: string;
  callback?: () => void;
};

export type MainPageState = NetworkState & {
  permissionContext: PermissionContext;
  alertData?: AlertData;
};
export interface MainPageModel {
  readonly core: PMaxCore;
  readonly state: MainPageState;
  readonly event: UpdateEventListener<MainPageModel>;

  readonly account: Account | null;
  readonly barModel: AppMenuBarModel;
  readonly headerModel: AppHeaderModel;
  readonly ordersModel: OrdersModel;
  readonly creativesModel: CreativesModel;
  readonly advertisersModel: AdvertisersModel;
  readonly agenciesModel: AgenciesModel;
  readonly accountsModel: AccountsModel;
  readonly retailMomoModel: RetailMomoModel;
  readonly mainPagePath: string;

  init(): Promise<void>;
  switchActor(actor: Actor): Promise<void>;
  setAlertData(data?: AlertData): void;
}

export interface MainPageProps {
  readonly model: MainPageModel;
  readonly authenticationManager: AuthenticationManager;
}

export class DefaultMainPageModel implements MainPageModel {
  pmaxCore: PMaxCore;
  modelEvent: FireableUpdateEventListener<MainPageModel>;

  error: Error | null;
  loading: boolean;
  menuBarModel: AppMenuBarModel;
  appHeaderModel: AppHeaderModel;
  errorCode?: number;
  ordersModel: OrdersModel;
  creativesModel: CreativesModel;
  advertisersModel: AdvertisersModel;
  permissionContext: PermissionContext;
  agenciesModel: AgenciesModel;
  accountsModel: AccountsModel;
  retailMomoModel: RetailMomoModel;
  mainPagePathMap: any;
  alertData?: AlertData;
  userStore: any;

  constructor(core: PMaxCore) {
    this.userStore = userStore;
    this.pmaxCore = core;
    this.error = null;
    this.loading = true;
    this.modelEvent = new FireableUpdateEventListener<MainPageModel>();
    this.menuBarModel = new DefaultAppMenuBarModel(defaultMenus());
    this.ordersModel = new DefaultOrdersModel(this.pmaxCore);
    this.creativesModel = new DefaultCreativesModel(this.pmaxCore);
    this.advertisersModel = new DefaultAdvertisersModel(this.pmaxCore);
    this.agenciesModel = new DefaultAgenciesModel(this.pmaxCore);
    this.accountsModel = new DefaultAccountsModel(this.pmaxCore);
    this.retailMomoModel = new DefaultRetailMomoModel(this.pmaxCore);
    this.appHeaderModel = new DefaultAppHeaderModel(
      this.pmaxCore.authenticationManager,
      this.pmaxCore.languageManager,
      this.menuBarModel,
      this,
    );
    this.permissionContext = {
      actor: core.authenticationManager.actor,
      agencyAddon: core.addonFeatureManager.addonFeature,
    };
    this.menuBarModel.updatePermissionContext(
      core.authenticationManager.actor,
      core.addonFeatureManager.addonFeature,
    );

    this.core.authenticationManager.event.add((manager) => {
      this.permissionContext = {
        actor: manager.actor,
        agencyAddon: this.core.addonFeatureManager.addonFeature,
      };
      this.refreshAllModel();
      this.menuBarModel.updatePermissionContext(
        manager.actor,
        this.core.addonFeatureManager.addonFeature,
      );
    });

    this.core.addonFeatureManager.event.add((manager) => {
      this.permissionContext = {
        actor: this.core.authenticationManager.actor,
        agencyAddon: manager.addonFeature,
      };
      this.refreshAllModel();
      this.menuBarModel.updatePermissionContext(
        this.core.authenticationManager.actor,
        manager.addonFeature,
      );
    });

    this.menuBarModel.expandChangeEvent.add(() => {
      this.updateState(false);
    });
    this.mainPagePathMap = {};
    const reportMainPageRole = [RoleNames.sysAdmin, RoleNames.adsReport];
    Object.values(RoleNames).forEach((roleName) => {
      reportMainPageRole.includes(roleName)
        ? (this.mainPagePathMap[roleName] = '/reports/performance')
        : (this.mainPagePathMap[roleName] = '/index');
    });
  }

  get mainPagePath() {
    if (config.mainPagePath) {
      return config.mainPagePath;
    }
    if (this.core.authenticationManager.actor) {
      return this.mainPagePathMap[
        this.core.authenticationManager.actor.roleName.toUpperCase()
      ];
    }
    return '/index';
  }

  get core(): PMaxCore {
    return this.pmaxCore;
  }

  get event(): UpdateEventListener<MainPageModel> {
    return this.modelEvent;
  }

  get state(): MainPageState {
    return {
      error: this.error,
      loading: this.loading,
      errorCode: this.errorCode,
      permissionContext: this.permissionContext,
      alertData: this.alertData,
    };
  }

  get account(): Account | null {
    return this.core.authenticationManager.account;
  }

  get barModel(): AppMenuBarModel {
    return this.menuBarModel;
  }

  get headerModel(): AppHeaderModel {
    return this.appHeaderModel;
  }

  refreshAllModel() {
    this.ordersModel = new DefaultOrdersModel(this.pmaxCore);
    this.creativesModel = new DefaultCreativesModel(this.pmaxCore);
    this.advertisersModel = new DefaultAdvertisersModel(this.pmaxCore);
    this.agenciesModel = new DefaultAgenciesModel(this.pmaxCore);
    this.accountsModel = new DefaultAccountsModel(this.pmaxCore);
    this.retailMomoModel = new DefaultRetailMomoModel(this.pmaxCore);
  }
  handleSetSeller = (newSeller: string) => {
    const { setSeller } = this.userStore.getState();
    setSeller(newSeller);
  };
  async init() {
    if (this.core.authenticationManager.logined === true) {
      this.updateState(true);
      try {
        await this.core.authenticationManager.fetchAccount();
        await this.core.accountManager.updateLocaleMeta(
          this.core.authenticationManager.actor,
        );
        this.core.addonFeatureManager.updateAddonFeature(
          this.core.accountManager.localeMeta
            ? this.core.accountManager.localeMeta.addonFeatures
            : {},
        );
        this.updateState(false);
        // 檢查 vendorNumber 是否包含 "TP"，有表示 3P
        const vendorNumber =
          this.core?.accountManager?.localeMeta?.vendorNumber;
        const containsTP = vendorNumber?.includes('TP') ? '3P' : '1P';
        this.handleSetSeller(containsTP);
      } catch (error) {
        this.updateState(false, error as Error);
      }
    }
  }

  async switchActor(actor: Actor) {
    this.updateState(true);
    this.errorCode = undefined;
    SessionStorageHelper.removeItem(SessionStorageItemKeys.ADVERTISER);
    SessionStorageHelper.removeItem(SessionStorageItemKeys.AGENCY);
    try {
      this.core.authenticationManager.switchActor(actor.id);
      await this.core.accountManager.updateLocaleMeta(actor);
      this.core.addonFeatureManager.updateAddonFeature(
        this.core.accountManager.localeMeta
          ? this.core.accountManager.localeMeta.addonFeatures
          : {},
      );
      this.updateState(false);
      // 檢查 vendorNumber 是否包含 "TP"，有表示 3P
      const vendorNumber = this.core?.accountManager?.localeMeta?.vendorNumber;
      const containsTP = vendorNumber?.includes('TP') ? '3P' : '1P';
      this.handleSetSeller(containsTP);
    } catch (error) {
      this.updateState(false, error as Error);
    }
  }

  setAlertData(data?: AlertData): void {
    this.alertData = data;
    this.modelEvent.fireEvent(this);
  }

  updateState(loading: boolean, error: Error | null = null) {
    this.error = error;
    this.loading = loading;
    this.modelEvent.fireEvent(this);
  }
}
